import { Injectable } from '@angular/core';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { CmxClientService, CmxHttpClient, getParameters } from '@cemex/cmx-client-layout';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Features } from '@ordersnproducts/features';
import { CxAlertService } from 'app/shared/cx-alert/cx-alert.service';
import { CxModalService } from 'app/shared/cx-modal/cx-modal.service';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { ORDER_EXPERIENCES } from '../../constants/order-experiences';
import { _safe } from '../../utils/safe-get.method';
import { CtdReceiveSuccessDialogComponent } from './dialogs/ctd-receive-success-dialog/ctd-receive-success-dialog.component';
import { CtdOrderDetailResponse } from './models/ctd-order';
import { CtdOrderDispatchesResponse } from './models/ctd-order-dispatches';
import OrderAction from './store/actions/ctd-order.actions';
import { CtdState } from './store/reducers';
import { CtdOrderEntity } from './store/reducers/ctd-order.reducer';
import { selectCtdOrderById, selectSelectedEntity } from './store/selectors/ctd-order.selector';
import { AnalyticsTrackingService } from 'app/services/analytics-tracking.service';
import { GoogleAnalyticsEventLabel } from 'app/modules/google-analytics-event-type';
import { ParameterCodes } from '@cemex-core/cmx-api';

export type FETCH_POLICY = 'cache-and-network' | 'cache-first' | 'network-only' | 'cache-only';
const ORDER_CTD_X_PARAMETER_DEFAULT_VALUE = 60;

@Injectable({
  providedIn: 'root',
})
export class CtdFacadeService {
  constructor(
    private cmxClientService: CmxClientService,
    private cxModalService: CxModalService,
    private cxAlertService: CxAlertService,
    private ts: TranslateService,
    private http: CmxHttpClient,
    private store: Store<CtdState>,
    private featureToggleService: FeatureToggleService,
    private analytics: AnalyticsTrackingService,
  ) {}

  public xMin$ = this.getCutOffParameter$();

  public getCtdOrderDetail(orderId: string | number) {
    return this.http.get<CtdOrderDetailResponse>(`/v1/odm/orders/${orderId}/details`);
  }

  public patchCtdOrderToConfirmed(orderId: string) {
    return this.http.patch<CtdOrderDispatchesResponse>(`/v1/odm/orders/${orderId}/dispatches/confirmed`, {});
  }

  public patchCtdOrderToReleased(orderId: string) {
    return this.http.patch<CtdOrderDispatchesResponse>(`/v1/odm/orders/${orderId}/dispatches/released`, {});
  }

  public watchOrderLoading(orderId: string) {
    this.store
      .select(selectCtdOrderById, { id: orderId })
      .pipe(
        map(({ isLoading }) => isLoading),
        takeWhile((isLoading) => isLoading, true),
      )
      .subscribe((isLoading) => {
        if (isLoading) {
          this.cmxClientService.showGlobalLoader();
        } else {
          this.cmxClientService.hideGlobalLoader();
        }
      });
  }

  public selectOrder$(orderId: string, fetchPolicy: FETCH_POLICY = 'cache-first'): Observable<CtdOrderEntity> {
    return this.store.select(selectCtdOrderById, { id: orderId }).pipe(
      tap((orderEntity) => {
        if (!orderEntity) {
          this.store.dispatch(OrderAction.initCtdOrder({ id: orderId }));
        }
      }),
      filter((orderEntity) => orderEntity),
      tap((orderEntity: CtdOrderEntity) => {
        if (!orderEntity.isLoading) {
          if (
            fetchPolicy === 'network-only' ||
            fetchPolicy === 'cache-and-network' ||
            (fetchPolicy === 'cache-first' && !orderEntity.isFetched)
          ) {
            this.store.dispatch(OrderAction.fetchCtdOrder({ id: orderId }));
          }
        }
      }),
      take(1),
      switchMap(() => this.store.select(selectCtdOrderById, { id: orderId })),
      filter(({ isLoading, isFetched }) => {
        switch (fetchPolicy) {
          case 'network-only': {
            return !isLoading && isFetched ? true : false;
          }

          case 'cache-first': {
            return isFetched ? true : !isLoading ? true : false;
          }

          case 'cache-and-network':
          case 'cache-only':
          default: {
            return true;
          }
        }
      }),
    );
  }

  public requestConfirmToDispatch(orderId: string) {
    this.store.dispatch(OrderAction.fetchCtdOrderConfirmed({ id: orderId }));
    this.watchOrderLoading(orderId);

    return this.store.select(selectCtdOrderById, { id: orderId }).pipe(
      filter((orderEntity) => !orderEntity.isLoading),
      tap((orderEntity) => {
        if (orderEntity.hasError) {
          this.cxAlertService.createAlert({
            message: this.ts.instant('ctd.alert.changes_save_failed_msg'),
            variant: 'error',
          });
        } else {
          this.cxAlertService.createAlert({
            message: this.ts.instant('ctd.alert.changes_save_success_msg'),
            variant: 'success',
          });
        }
        this.analytics.confirmToDispatch().confirmToDispatchOrderDetail(orderEntity);
      }),
    );
  }

  public requestReadyToDispatch(orderId: string, location: GoogleAnalyticsEventLabel) {
    this.store.dispatch(OrderAction.fetchCtdOrderReleased({ id: orderId }));
    this.watchOrderLoading(orderId);

    return this.store.select(selectCtdOrderById, { id: orderId }).pipe(
      filter((orderEntity) => !orderEntity.isLoading),
      tap((orderEntity) => {
        if (orderEntity.hasError) {
          this.cxAlertService.createAlert({
            message: this.ts.instant('ctd.alert.changes_save_failed_msg'),
            variant: 'error',
          });
        } else {
          this.cxModalService.createModal(CtdReceiveSuccessDialogComponent, {
            inputs: { orderCode: orderEntity.payload.orderCode },
          });
        }
        this.analytics.confirmToDispatch().readyToReceive(orderEntity, location);
      }),
    );
  }

  public checkOrderState(orderId: string) {
    this.store.dispatch(OrderAction.fetchCtdOrder({ id: orderId }));
    this.watchOrderLoading(orderId);

    return this.store.select(selectCtdOrderById, { id: orderId }).pipe(
      filter((orderEntity) => !orderEntity.isLoading),
      tap((orderEntity) => {
        if (orderEntity.hasError) {
          this.cxAlertService.createAlert({
            message: this.ts.instant('ctd.alert.changes_save_failed_msg'),
            variant: 'error',
          });
        }
      }),
    );
  }

  public checkFeatureEnabled() {
    return this.featureToggleService.isFeatureEnabled(Features.ORDER_CONFIRM_TO_DISPATCH);
  }

  public checkExperience(experienceCode: ORDER_EXPERIENCES) {
    return [ORDER_EXPERIENCES.RMX_LEGACY, ORDER_EXPERIENCES.RMX_UNIVERSAL_LINK].includes(
      experienceCode as ORDER_EXPERIENCES,
    );
  }

  public async getOrderRequestConfirmToDispatch() {
    return this.store
      .select(selectSelectedEntity)
      .pipe(
        map((ctdOrderEntity) =>
          _safe<Partial<CtdOrderDispatchesResponse> | boolean>(() => ctdOrderEntity.payload.confirmToDispatch, false),
        ),
        take(1),
      )
      .toPromise();
  }

  /**
   * Retrieves the cutoff parameter value for Confirm to Dispatch in minutes.
   * Selects the parameters from the store and finds the parameter with the description
   * matching `OT_CREATEORDER_CONFIRMTODISPATCH_CUTOFFTIME_MINS`. If not found, returns the default value.
   *
   * @returns Observable<number> - The cutoff time in minutes.
   */
  public getCutOffParameter$(): Observable<number> {
    return this.store.select(getParameters).pipe(
      map((parameters) => {
        const parameter = parameters.find(
          (param) => param.parameterDesc === ParameterCodes.OT_CREATEORDER_CONFIRMTODISPATCH_CUTOFFTIME_MINS,
        );
        const xMin = parameter ? Number(parameter.parameterValue) : ORDER_CTD_X_PARAMETER_DEFAULT_VALUE;
        return xMin;
      }),
    );
  }
}
